import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { format, isToday, isYesterday } from 'date-fns';
import Icon from 'components/AppIcon';
import ProfileAvatar from '../profile/ProfileAvatar';
import MessageReactions from './MessageReactions';
import MessageActions from './MessageActions';
import DiscordIndicator from './DiscordIndicator';

const ModernMessageBubble = React.forwardRef(({ 
  message, 
  previousMessage, 
  nextMessage, 
  currentUser, 
  onReply, 
  onSwipeReply,
  onContextMenu,
  isLastInGroup = false,
  showAvatar = true,
  showTimestamp = true 
}, ref) => {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const bubbleRef = useRef(null);
  const contextMenuRef = useRef(null);
  
  const isOwnMessage = message.author_id === currentUser.uid;
  const isConsecutive = previousMessage?.author_id === message.author_id && 
    (message.created_at - previousMessage.created_at) < 300000; // 5 minutes

  // Swipe gesture for mobile reply
  const x = useMotionValue(0);
  const replyThreshold = 50;
  const replyProgress = useTransform(x, [0, replyThreshold], [0, 1]);
  
  const handlePanEnd = (event, info) => {
    if (Math.abs(info.offset.x) > replyThreshold) {
      onSwipeReply?.(message);
    }
    // Always animate back to original position
    x.stop();
    x.set(0);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
    onContextMenu?.(message, { x: e.clientX, y: e.clientY });
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target) && showContextMenu) {
        setShowContextMenu(false);
      }
    };

    if (showContextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showContextMenu]);

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'text-red-400';
      case 'writer': return 'text-green-400';
      case 'qa': return 'text-blue-400';
      case 'client': return 'text-purple-400';
      default: return 'text-gray-400';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return 'Shield';
      case 'writer': return 'PenTool';
      case 'qa': return 'CheckCircle';
      case 'client': return 'User';
      default: return 'User';
    }
  };

  const formatMessageTime = (date) => {
    if (isToday(date)) return format(date, 'HH:mm');
    if (isYesterday(date)) return `Yesterday ${format(date, 'HH:mm')}`;
    return format(date, 'MMM dd, HH:mm');
  };

  const MessageStatus = ({ message, isOwnMessage }) => {
    if (!isOwnMessage) return null;
    
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center gap-1 mt-1"
      >
        {message._status === 'sending' && (
          <Icon name="Clock" size={14} className="text-white/40" />
        )}
        {message._status === 'sent' && (
          <Icon name="Check" size={14} className="text-green-400" />
        )}
        {message._status === 'failed' && (
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
          >
            <Icon name="AlertCircle" size={14} className="text-red-400" />
          </motion.div>
        )}
        {!message._optimistic && !message._status && message.id && !message.id.startsWith('temp-') && (
          <Icon name="CheckCheck" size={14} className="text-blue-400" />
        )}
      </motion.div>
    );
  };

  const ReplyPreview = ({ parentMessage }) => {
    if (!parentMessage) return null;
    
    return (
      <motion.div 
        initial={{ opacity: 0, y: -5 }}
        animate={{ opacity: 1, y: 0 }}
        className={`p-2 mb-2 rounded-lg border-l-3 ${
          isOwnMessage 
            ? 'bg-white/10 border-white/30' 
            : 'bg-primary/10 border-primary/50'
        }`}
      >
        <div className="flex items-center gap-2 mb-1">
          <ProfileAvatar
            user={parentMessage}
            size="xs"
            showStatus={false}
            showRole={true}
          />
          <span className="text-xs font-medium text-white/70">
            {parentMessage.author_name}
          </span>
        </div>
        <p className="text-xs text-white/60 line-clamp-2">
          {parentMessage.content}
        </p>
      </motion.div>
    );
  };

  return (
    <motion.div
      ref={ref}
      className={`flex gap-3 group relative ${
        isOwnMessage ? 'flex-row-reverse' : 'flex-row'
      } ${isConsecutive ? 'mt-1' : 'mt-4'}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onContextMenu={handleContextMenu}
    >
      {/* Swipe Reply Indicator */}
      <motion.div
        className={`absolute ${isOwnMessage ? 'right-full mr-2' : 'left-full ml-2'} top-1/2 -translate-y-1/2`}
        style={{ opacity: replyProgress }}
      >
        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
          <Icon name="Reply" size={16} className="text-primary" />
        </div>
      </motion.div>

      {/* Avatar */}
      {showAvatar && !isConsecutive && (
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        >
          <ProfileAvatar
            user={message}
            size="md"
            showStatus={false}
            showRole={false}
          />
        </motion.div>
      )}

      {/* Spacer for consecutive messages */}
      {!showAvatar || isConsecutive ? (
        <div className="w-10 flex-shrink-0" />
      ) : null}

      {/* Message Container */}
      <motion.div
        ref={bubbleRef}
        drag="x"
        dragConstraints={{ left: isOwnMessage ? -100 : 0, right: isOwnMessage ? 0 : 100 }}
        dragElastic={0.2}
        style={{ x }}
        onPanEnd={handlePanEnd}
        animate={{ x: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 25 }}
        className={`flex-1 max-w-xs lg:max-w-sm xl:max-w-md ${
          isOwnMessage ? 'text-right' : 'text-left'
        }`}
      >
        {/* Author name for first message in group */}
        {!isOwnMessage && !isConsecutive && (
          <motion.div 
            className="mb-1 ml-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <span className={`text-xs font-medium ${getRoleColor(message.author_role)}`}>
              {message.author_name}
            </span>
          </motion.div>
        )}

        {/* Message Bubble */}
        <motion.div
          className={`relative inline-block p-3 max-w-full ${
            isOwnMessage
              ? 'bg-gradient-to-br from-primary to-primary/80 text-white shadow-lg'
              : 'bg-white/10 backdrop-blur-sm text-white border border-white/20'
          } ${
            isConsecutive && !isLastInGroup
              ? 'rounded-2xl'
              : isOwnMessage
              ? 'rounded-2xl rounded-br-md'
              : 'rounded-2xl rounded-bl-md'
          }`}
          whileHover={{ scale: 1.02 }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        >
          {/* Reply Preview */}
          {message.parent_id && (
            <ReplyPreview parentMessage={message.parentMessage} />
          )}

          {/* Message Content */}
          <motion.p 
            className="text-sm leading-relaxed whitespace-pre-wrap break-words"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            {message.content}
          </motion.p>

          {/* Message Reactions */}
          {message.reactions && Object.keys(message.reactions).length > 0 && (
            <div className="mt-2">
              <MessageReactions
                messageId={message.id}
                reactions={message.reactions}
                size="sm"
              />
            </div>
          )}

          {/* Message Actions - Positioned to the side */}
          <MessageActions
            message={message}
            onReply={() => onReply(message)}
            canEdit={message.author_id === currentUser.uid}
            canDelete={message.author_id === currentUser.uid}
          />
        </motion.div>

        {/* External Status & Timestamp */}
        <div className={`flex items-center gap-2 mt-1 text-xs text-white/50 ${
          isOwnMessage ? 'justify-end' : 'justify-start'
        } ${isOwnMessage ? 'mr-3' : 'ml-3'}`}>
          {showTimestamp && (
            <span>{formatMessageTime(message.created_at)}</span>
          )}
          {message.edited && (
            <span className="italic">(edited)</span>
          )}
          <DiscordIndicator message={message} />
          <MessageStatus message={message} isOwnMessage={isOwnMessage} />
        </div>
      </motion.div>

      {/* Context Menu */}
      <AnimatePresence>
        {showContextMenu && (
          <motion.div
            ref={contextMenuRef}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed bg-black/90 backdrop-blur-md border border-white/20 rounded-lg shadow-xl z-50 p-1"
            style={{
              left: contextMenuPosition.x,
              top: contextMenuPosition.y,
            }}
            onMouseLeave={() => setShowContextMenu(false)}
          >
            <button
              onClick={() => {
                onReply(message);
                setShowContextMenu(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md w-full text-left"
            >
              <Icon name="Reply" size={14} />
              Reply
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(message.content);
                setShowContextMenu(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md w-full text-left"
            >
              <Icon name="Copy" size={14} />
              Copy
            </button>
            {message.author_id === currentUser.uid && (
              <>
                <button
                  onClick={() => setShowContextMenu(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-white hover:bg-white/10 rounded-md w-full text-left"
                >
                  <Icon name="Edit" size={14} />
                  Edit
                </button>
                <button
                  onClick={() => setShowContextMenu(false)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-md w-full text-left"
                >
                  <Icon name="Trash2" size={14} />
                  Delete
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

ModernMessageBubble.displayName = 'ModernMessageBubble';

export default ModernMessageBubble;